import cls from "classnames";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Step0FormData } from "../types";
// components
import { Button, Form, Input } from "antd";

export const Step0Form: React.FC<{
  data: Step0FormData;
  visible: boolean;
  onNext: (data: Step0FormData) => void;
  onCancel: () => void;
}> = ({ data, visible, onNext, onCancel }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<Step0FormData>();

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(data);
  }, [form, data]);

  return (
    <Form
      form={form}
      labelCol={{ span: 4 }}
      className={cls(["mt:16", { hidden: !visible }])}
      onFinish={(values) => {
        onNext(values);
      }}
    >
      <Form.Item
        label={t("domains.Tenant.tenantUsername")}
        name={["user", "username"]}
        rules={[
          {
            required: true,
            message: t("systems.Error.cannotBeEmpty", {
              field: t("domains.Tenant.tenantUsername"),
            }),
          },
        ]}
      >
        <Input
          placeholder={t("systems.Placeholder.pleaseInput", {
            field: t("domains.Tenant.tenantUsername"),
          })}
        />
      </Form.Item>

      <Form.Item
        required
        className="mb:0"
        label={t("domains.Tenant.tenantPassword")}
      >
        <Form.Item
          name={["user", "password1"]}
          rules={[
            {
              required: true,
              message: t("systems.Error.cannotBeEmpty", {
                field: t("domains.Tenant.tenantPassword"),
              }),
            },
          ]}
        >
          <Input
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: t("domains.Tenant.tenantPassword"),
            })}
          />
        </Form.Item>

        <Form.Item
          name={["user", "password2"]}
          dependencies={[["user", "password1"]]}
          rules={[
            {
              required: true,
              message: t("systems.Placeholder.pleaseInputAgain", {
                field: t("domains.Tenant.tenantPassword"),
              }),
            },
            {
              validator(_, value: string) {
                if (
                  value.length &&
                  value !== form.getFieldValue(["user", "password1"])
                )
                  return Promise.reject(
                    t("systems.Error.confirmPasswordNotMatch")
                  );
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder={t("systems.Placeholder.pleaseInputAgain", {
              field: t("domains.Tenant.tenantPassword"),
            })}
          />
        </Form.Item>
      </Form.Item>

      <Form.Item
        label={t("domains.Tenant.tenantName")}
        name={["user", "name"]}
        rules={[
          {
            required: true,
            message: t("systems.Error.cannotBeEmpty", {
              field: t("domains.Tenant.tenantName"),
            }),
          },
        ]}
      >
        <Input
          placeholder={t("systems.Placeholder.pleaseInput", {
            field: t("domains.Tenant.tenantName"),
          })}
        />
      </Form.Item>

      <Form.Item
        label={t("domains.Tenant.tenantCallback")}
        name={["base", "callback"]}
      >
        <Input
          placeholder={t("systems.Placeholder.pleaseInput", {
            field: t("domains.Tenant.tenantCallback"),
          })}
        />
      </Form.Item>

      <Form.Item
        label={t("domains.Tenant.tenantAddress")}
        name={["base", "address"]}
      >
        <div className="flex ai:center gap:8">
          <div>USDT</div>
          <Input
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: ` USDT ${t("domains.Tenant.tenantAddress")}`,
            })}
          />
        </div>
      </Form.Item>

      <Form.Item
        label={t("domains.Tenant.agencyRate")}
        name={["base", "agency_rate"]}
      >
        <div className="flex ai:center gap:8">
          <Input
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: t("domains.Tenant.agencyRate"),
            })}
          />
          <div>%</div>
        </div>
      </Form.Item>

      <Form.Item>
        <div className="flex jc:end gap:8">
          <Button htmlType="button" onClick={onCancel}>
            {t("systems.Action.cancel")}
          </Button>
          <Button type="primary" htmlType="submit">
            {t("systems.Action.next")}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};
